import React, { useEffect } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import AppTable from "components/Shared/AppTable";
import moment from "moment";
import { useTablePagination } from "hooks";
import { useLazyGetVehicleHiringEntryChangeLogQuery } from "store/services/vehicleHiringEntryApi";

// Maps backend field names to the labels shown in the "Changed Field"
// column. Backend field identifiers stay as-is (the model isn't
// renamed); only the UI display is translated here.
const FIELD_LABELS = {
    responsibleUser: "Approve / Submitted To",
};

const formatFieldLabel = (field) => {
    if (!field) return "";
    if (FIELD_LABELS[field]) return FIELD_LABELS[field];
    return field.replace(/_/g, " ");
};

const formatChangeValue = (field, value, resolvedUser = null) => {
    if (value === null || value === undefined) return "—";
    if (field === "hiringDates" && Array.isArray(value)) {
        if (!value.length) return "—";
        const sorted = [...value].sort((a, b) => new Date(a) - new Date(b));
        return (
            <Box sx={{ maxWidth: 320 }}>
                {sorted
                    .map((d) => moment(d).format("MMM D"))
                    .join(", ")}
                <Typography variant="caption" color="text.secondary" display="block">
                    {sorted.length} day{sorted.length !== 1 ? "s" : ""}
                </Typography>
            </Box>
        );
    }
    if (field === "bankAccountDetails" && typeof value === "object") {
        return (
            <Box>
                <Typography variant="caption" display="block">Bank: {value.bankName || "N/A"}</Typography>
                <Typography variant="caption" display="block">Acc. Name: {value.accountName || "N/A"}</Typography>
                <Typography variant="caption" display="block">Acc. No: {value.accountNumber || "N/A"}</Typography>
            </Box>
        );
    }
    if (field === "responsibleUser") {
        if (value && typeof value === "object" && !Array.isArray(value) && value.name) {
            return value.name;
        }
        return resolvedUser?.name || String(value);
    }
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const VehicleHiringEntryChangeLogs = ({ vehicleHiringEntryId }) => {
    const { currentPage, perPage } = useTablePagination(10);
    const [fetchChangeLog, { data, isLoading, isFetching, error }] =
        useLazyGetVehicleHiringEntryChangeLogQuery();

    useEffect(() => {
        if (!vehicleHiringEntryId) return;
        fetchChangeLog({
            id: vehicleHiringEntryId,
            page: currentPage || 1,
            limit: perPage || 10,
        });
    }, [vehicleHiringEntryId, currentPage, perPage, fetchChangeLog]);

    if (isLoading || isFetching) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" padding={4}>
                <CircularProgress size={24} />
                <Typography variant="body2" marginLeft={1}>
                    Loading Change Logs...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box padding={2}>
                <Typography color="error" variant="body2">
                    Error loading change logs: {error?.data?.message || "Something went wrong"}
                </Typography>
            </Box>
        );
    }

    const changeLogs = data?.data || [];

    const columns = [
        {
            name: "Changed Field",
            selector: (row) => row.field || "",
            sortable: true,
            cell: (row) => (
                <Typography fontWeight="500" sx={{ textTransform: "capitalize" }}>
                    {formatFieldLabel(row.field)}
                </Typography>
            ),
        },
        {
            name: "Old Value",
            selector: (row) => row.oldValue,
            cell: (row) => formatChangeValue(row.field, row.oldValue, row.responsibleUserSnapshot?.old),
        },
        {
            name: "New Value",
            selector: (row) => row.newValue,
            cell: (row) => formatChangeValue(row.field, row.newValue, row.responsibleUserSnapshot?.new),
        },
        {
            name: "Changed By",
            selector: (row) => row.changedBy?.name,
            sortable: true,
            cell: (row) => row.changedBy?.name || "Unknown",
        },
        {
            name: "Time",
            selector: (row) => row.changedAt,
            sortable: true,
            cell: (row) => row.changedAt ? moment(row.changedAt).format("MMM D, YYYY HH:mm") : "—",
        },
    ];

    return (
        <Box>
            <AppTable
                columns={columns}
                data={changeLogs}
                progressPending={false}
                paginationTotalRows={data?.totalItems || 0}
                highlightOnHover
                striped
            />
        </Box>
    );
};

export default VehicleHiringEntryChangeLogs;